import { db } from '../firebase/config';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs, getDoc,
  query, where, orderBy, serverTimestamp, writeBatch
} from 'firebase/firestore';

const COLLECTIONS = {
  HOSTELS: 'hostels',
  HOSTEL_BLOCKS: 'hostelBlocks',
  HOSTEL_FLOORS: 'hostelFloors',
  HOSTEL_ROOMS: 'hostelRooms',
  HOSTEL_BEDS: 'hostelBeds',
  HOSTEL_ALLOCATIONS: 'hostelAllocations',
  HOSTEL_TRANSFERS: 'hostelTransfers',
  MESS_PLANS: 'messPlans',
  STUDENT_MESS_PLANS: 'studentMessPlans',
  HOSTEL_FEE_STRUCTURES: 'hostelFeeStructures',
  MESS_FEE_STRUCTURES: 'messFeeStructures',
  HOSTEL_NOTICES: 'hostelNotices',
  HOSTEL_COMPLAINTS: 'hostelComplaints',
  STAFF_HOSTEL_ASSIGNMENTS: 'staffHostelAssignments',
};

// ─── HOSTELS ──────────────────────────────────────────────────────────────────
export const createHostel = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTELS), {
    ...data, coachingId, status: 'active', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const getHostels = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.HOSTELS), where('coachingId', '==', coachingId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateHostel = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.HOSTELS, id), { ...data, updatedAt: serverTimestamp() });
};

export const deleteHostel = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.HOSTELS, id));
};

// ─── BLOCKS ───────────────────────────────────────────────────────────────────
export const createBlock = async (coachingId, hostelId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTEL_BLOCKS), {
    ...data, coachingId, hostelId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getBlocks = async (hostelId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_BLOCKS), where('hostelId', '==', hostelId), orderBy('createdAt'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteBlock = async (id) => { await deleteDoc(doc(db, COLLECTIONS.HOSTEL_BLOCKS, id)); };

// ─── FLOORS ───────────────────────────────────────────────────────────────────
export const createFloor = async (coachingId, hostelId, blockId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTEL_FLOORS), {
    ...data, coachingId, hostelId, blockId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getFloors = async (blockId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_FLOORS), where('blockId', '==', blockId), orderBy('createdAt'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteFloor = async (id) => { await deleteDoc(doc(db, COLLECTIONS.HOSTEL_FLOORS, id)); };

// ─── ROOMS ────────────────────────────────────────────────────────────────────
export const createRoom = async (coachingId, hostelId, blockId, floorId, data) => {
  const batch = writeBatch(db);
  const roomRef = doc(collection(db, COLLECTIONS.HOSTEL_ROOMS));
  batch.set(roomRef, {
    ...data, coachingId, hostelId, blockId, floorId,
    occupiedBeds: 0, createdAt: serverTimestamp(),
  });

  // Auto-generate beds
  const capacity = data.capacity || 1;
  for (let i = 1; i <= capacity; i++) {
    const bedRef = doc(collection(db, COLLECTIONS.HOSTEL_BEDS));
    batch.set(bedRef, {
      coachingId, hostelId, blockId, floorId, roomId: roomRef.id,
      bedNumber: i, status: 'vacant', studentId: null, createdAt: serverTimestamp(),
    });
  }

  await batch.commit();
  return roomRef.id;
};

export const getRooms = async (floorId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_ROOMS), where('floorId', '==', floorId), orderBy('createdAt'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getRoomsByHostel = async (hostelId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_ROOMS), where('hostelId', '==', hostelId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── BEDS ─────────────────────────────────────────────────────────────────────
export const getBeds = async (roomId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_BEDS), where('roomId', '==', roomId), orderBy('bedNumber'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getVacantBeds = async (roomId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_BEDS), where('roomId', '==', roomId), where('status', '==', 'vacant'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── ALLOCATIONS ──────────────────────────────────────────────────────────────
export const allocateStudent = async (coachingId, data) => {
  const batch = writeBatch(db);

  // Create allocation record
  const allocRef = doc(collection(db, COLLECTIONS.HOSTEL_ALLOCATIONS));
  batch.set(allocRef, {
    ...data, coachingId, status: 'active', allocatedAt: serverTimestamp(), createdAt: serverTimestamp(),
  });

  // Update bed status
  const bedRef = doc(db, COLLECTIONS.HOSTEL_BEDS, data.bedId);
  batch.update(bedRef, { status: 'occupied', studentId: data.studentId });

  // Update room occupancy
  const roomRef = doc(db, COLLECTIONS.HOSTEL_ROOMS, data.roomId);
  const roomSnap = await getDoc(roomRef);
  if (roomSnap.exists()) {
    batch.update(roomRef, { occupiedBeds: (roomSnap.data().occupiedBeds || 0) + 1 });
  }

  await batch.commit();
  return allocRef.id;
};

export const getAllocations = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_ALLOCATIONS), where('coachingId', '==', coachingId), where('status', '==', 'active'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const vacateStudent = async (allocationId, bedId, roomId, remarks) => {
  const batch = writeBatch(db);

  batch.update(doc(db, COLLECTIONS.HOSTEL_ALLOCATIONS, allocationId), {
    status: 'vacated', vacatedAt: serverTimestamp(), vacateRemarks: remarks,
  });
  batch.update(doc(db, COLLECTIONS.HOSTEL_BEDS, bedId), { status: 'vacant', studentId: null });

  const roomSnap = await getDoc(doc(db, COLLECTIONS.HOSTEL_ROOMS, roomId));
  if (roomSnap.exists()) {
    const occ = Math.max((roomSnap.data().occupiedBeds || 1) - 1, 0);
    batch.update(doc(db, COLLECTIONS.HOSTEL_ROOMS, roomId), { occupiedBeds: occ });
  }

  await batch.commit();
};

// ─── TRANSFERS ────────────────────────────────────────────────────────────────
export const transferStudent = async (coachingId, data) => {
  const batch = writeBatch(db);

  // Record transfer
  const tRef = doc(collection(db, COLLECTIONS.HOSTEL_TRANSFERS));
  batch.set(tRef, { ...data, coachingId, transferredAt: serverTimestamp() });

  // Release old bed
  batch.update(doc(db, COLLECTIONS.HOSTEL_BEDS, data.oldBedId), { status: 'vacant', studentId: null });
  const oldRoomSnap = await getDoc(doc(db, COLLECTIONS.HOSTEL_ROOMS, data.oldRoomId));
  if (oldRoomSnap.exists()) {
    batch.update(doc(db, COLLECTIONS.HOSTEL_ROOMS, data.oldRoomId), { occupiedBeds: Math.max((oldRoomSnap.data().occupiedBeds || 1) - 1, 0) });
  }

  // Occupy new bed
  batch.update(doc(db, COLLECTIONS.HOSTEL_BEDS, data.newBedId), { status: 'occupied', studentId: data.studentId });
  const newRoomSnap = await getDoc(doc(db, COLLECTIONS.HOSTEL_ROOMS, data.newRoomId));
  if (newRoomSnap.exists()) {
    batch.update(doc(db, COLLECTIONS.HOSTEL_ROOMS, data.newRoomId), { occupiedBeds: (newRoomSnap.data().occupiedBeds || 0) + 1 });
  }

  // Update allocation
  batch.update(doc(db, COLLECTIONS.HOSTEL_ALLOCATIONS, data.allocationId), {
    hostelId: data.newHostelId, blockId: data.newBlockId, floorId: data.newFloorId,
    roomId: data.newRoomId, bedId: data.newBedId, updatedAt: serverTimestamp(),
  });

  await batch.commit();
};

// ─── MESS PLANS ───────────────────────────────────────────────────────────────
export const createMessPlan = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.MESS_PLANS), {
    ...data, coachingId, status: 'active', createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getMessPlans = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.MESS_PLANS), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateMessPlan = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.MESS_PLANS, id), { ...data, updatedAt: serverTimestamp() });
};

export const deleteMessPlan = async (id) => { await deleteDoc(doc(db, COLLECTIONS.MESS_PLANS, id)); };

export const assignMessPlan = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.STUDENT_MESS_PLANS), {
    ...data, coachingId, status: 'active', createdAt: serverTimestamp(),
  });
  return ref.id;
};

// ─── FEE STRUCTURES ───────────────────────────────────────────────────────────
export const createHostelFeeStructure = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTEL_FEE_STRUCTURES), {
    ...data, coachingId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getHostelFeeStructures = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_FEE_STRUCTURES), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createMessFeeStructure = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.MESS_FEE_STRUCTURES), {
    ...data, coachingId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getMessFeeStructures = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.MESS_FEE_STRUCTURES), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── NOTICES ──────────────────────────────────────────────────────────────────
export const createHostelNotice = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTEL_NOTICES), {
    ...data, coachingId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getHostelNotices = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_NOTICES), where('coachingId', '==', coachingId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteHostelNotice = async (id) => { await deleteDoc(doc(db, COLLECTIONS.HOSTEL_NOTICES, id)); };

// ─── COMPLAINTS ───────────────────────────────────────────────────────────────
export const createHostelComplaint = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.HOSTEL_COMPLAINTS), {
    ...data, coachingId, status: 'open', createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getHostelComplaints = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.HOSTEL_COMPLAINTS), where('coachingId', '==', coachingId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateComplaintStatus = async (id, status, notes) => {
  await updateDoc(doc(db, COLLECTIONS.HOSTEL_COMPLAINTS, id), {
    status, resolutionNotes: notes || '', updatedAt: serverTimestamp(),
  });
};

// ─── STAFF ASSIGNMENT ─────────────────────────────────────────────────────────
export const assignStaffToHostel = async (coachingId, data) => {
  const ref = await addDoc(collection(db, COLLECTIONS.STAFF_HOSTEL_ASSIGNMENTS), {
    ...data, coachingId, createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const getStaffAssignments = async (coachingId) => {
  const q = query(collection(db, COLLECTIONS.STAFF_HOSTEL_ASSIGNMENTS), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── REPORTS ──────────────────────────────────────────────────────────────────
export const getOccupancyReport = async (coachingId) => {
  const bedsQ = query(collection(db, COLLECTIONS.HOSTEL_BEDS), where('coachingId', '==', coachingId));
  const snap = await getDocs(bedsQ);
  const beds = snap.docs.map(d => d.data());
  return {
    total: beds.length,
    occupied: beds.filter(b => b.status === 'occupied').length,
    vacant: beds.filter(b => b.status === 'vacant').length,
  };
};

export default {
  COLLECTIONS,
  createHostel, getHostels, updateHostel, deleteHostel,
  createBlock, getBlocks, deleteBlock,
  createFloor, getFloors, deleteFloor,
  createRoom, getRooms, getRoomsByHostel,
  getBeds, getVacantBeds,
  allocateStudent, getAllocations, vacateStudent, transferStudent,
  createMessPlan, getMessPlans, updateMessPlan, deleteMessPlan, assignMessPlan,
  createHostelFeeStructure, getHostelFeeStructures,
  createMessFeeStructure, getMessFeeStructures,
  createHostelNotice, getHostelNotices, deleteHostelNotice,
  createHostelComplaint, getHostelComplaints, updateComplaintStatus,
  assignStaffToHostel, getStaffAssignments,
  getOccupancyReport,
};
